import { ref } from 'vue'
import axios from 'axios'
import CONSTANTS from '../data/constants'

export default function () {
    const isMobile = ref(false)
    const termOptions = CONSTANTS.TERM_OPTIONS
    const mileageOptions = CONSTANTS.MILEAGE_OPTIONS
    const frequencyOptions = CONSTANTS.FREQUENCY_OPTIONS
    const financeProducts = CONSTANTS.FINANCE_PRODUCTS
    const interestRate = CONSTANTS.INITIAL_INTEREST_RATE
    const establishmentFees = CONSTANTS.ESTABLISHMENT_FEES
    const currencyOptions = CONSTANTS.CURRENCY_FIELD_OPTIONS
    const cancelInterest = ref(null)
    const depositLimit = ref(0)
    const deposit = ref(null)
    const showDepositTip = ref(false)
    const amountCallPromises = ref([])
    const repayments = ref(0)
    const calculatorRequestProcessing = ref(false)
    const total = ref({})

    function onResize () {
        isMobile.value = window.innerWidth < CONSTANTS.MOBILE_BREAKPOINT
    }

    function getDayInAWeek (freq) {
      if (freq === 'M') {
        return getNextMonthDate()
      }
      if (freq === 'B') {
        return new Date(new Date().setDate(new Date().getDate() + 14))
          .toISOString()
          .slice(0, 10)
      }
      return new Date(new Date().setDate(new Date().getDate() + 7))
        .toISOString()
        .slice(0, 10)
    }

    function getNextMonthDate () {
        const today = new Date()
        const currentDay = today.getDate()
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, currentDay)
        // Check if the resulting date is in the same month as intended
        if (nextMonth.getMonth() !== (today.getMonth() + 1) % 12) {
          // Adjust to the last day of the next month
          nextMonth.setMonth(nextMonth.getMonth() + 1, 0)
        }
        const nzOffsetMinutes = 12 * 60 // New Zealand timezone offset in minutes
        const nzTimestamp = nextMonth.getTime() + nzOffsetMinutes * 60 * 1000
        const nzDate = new Date(nzTimestamp)
        return nzDate.toISOString().split('T')[0]
    }

    function getToday () {
      return new Date().toISOString().slice(0, 10)
    }

    function getCurrency (value, noDecimals) {
      if (!value) {
          return ''
      }
      if (noDecimals) {
          return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
          }).format(value)
      }
      return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
      }).format(value)
    }

    async function executeCalculation (data) {
      calculatorRequestProcessing.value = true
      const loanAmount =
        parseFloat(data.rrp) + establishmentFees - deposit.value

      const selectedTermMultiplier = data.selectedFreq === 'B'
        ? 26
        : data.selectedFreq === 'M' ? 12 : 52

      const calculatorPayload = {
        request_id: new Date().getTime(),
        loan_amt: loanAmount,
        amount_finaced: loanAmount,
        int_rate: data.interestRate,
        term: (data.selectedTerm / 12) * selectedTermMultiplier,
        payment_method: 'R',
        payment_freq: data.selectedFreq,
        first_payment_dt: getDayInAWeek(data.selectedFreq),
        contract_dt: getToday(),
        financed_fees: 0,
        calculatorAmendement: []
      }
      if (data.selectedProduct === 'iown') {
        try {
          amountCallPromises.value.push(caclculateGFV(data))
          Promise.all(amountCallPromises.value).then(async (r) => {
            if (r.length > 0 && r[r.length - 1] && r[r.length - 1].data) {
              const gfvResponse = r[r.length - 1]
              const gfv = gfvResponse.data.Result[0].gfvValue
              depositLimit.value = gfv ? parseFloat(data.rrp) - gfv : parseFloat(data.rrp)
              if (data.deposit > depositLimit.value) {
                deposit.value = Math.floor(depositLimit.value)
                return false
              }
              calculatorPayload.calculatorAmendement = [
                {
                  WbsSeqNo: data.selectedTerm / 12 * 52,
                  WbsAmount: gfv,
                  WbsInclusive: 'Y'
                }
              ]
              const interestResponse = await caclculateInterest(calculatorPayload, data.clientId)
              cancelInterest.value = null
              const totalInit = 0
              const interestInit = 0
              repayments.value = interestResponse.data.paymentDetails.PaymentDetails[0].Amount

              const totalLoan = interestResponse.data.paymentDetails.PaymentDetails.reduce(
                (accumulator, currentValue) => { return accumulator + currentValue.Amount }, totalInit
              )

              const interestLoan = interestResponse.data.paymentDetails.PaymentDetails.reduce(
                (a, c) => { return a + c.Interest }, interestInit
              )

              total.value = {
                interest: interestLoan,
                gfv: gfv,
                loan: totalLoan
              }
            }
          }).catch((e) => { console.log('err:', e) })
            } catch (error) { console.log('Error from execute calculation #1 : ', error) }
          } else {
            try {
              const interestResponse = await caclculateInterest(calculatorPayload, data.clientId)
              cancelInterest.value = null

              const totalInit = 0
              const interestInit = 0

              repayments.value = interestResponse.data.paymentDetails.PaymentDetails[0].Amount

              depositLimit.value = parseFloat(data.rrp)

              const totalLoan = interestResponse.data.paymentDetails.PaymentDetails.reduce(
                (accumulator, currentValue) => { return accumulator + currentValue.Amount }, totalInit
              )

              const interestLoan = interestResponse.data.paymentDetails.PaymentDetails.reduce(
                (a, c) => { return a + c.Interest }, interestInit
              )

              total.value = {
                interest: interestLoan,
                gfv: 0,
                deposit: deposit.value,
                loan: totalLoan
              }
            } catch (error) { console.log('Error from execute calculation #2 : ', error) }
          }
    }

    async function caclculateGFV (data) {
        const urlToGetGFV = process.env.VUE_APP_GFV_URL
        const apiRequestHeaders = {
          'content-type': 'application/json',
          'x-ibm-client-id': data.clientId,
          client_id: data.clientId
        }

        const gfvPayload = {
          request_id: new Date().getTime(),
          vehicleId: data.code,
          term: data.selectedTerm,
          mileage: (data.selectedMileage * data.selectedTerm / 12).toString()
        }

        // cancelGFV.value = axios.CancelToken.source()
        return axios.post(urlToGetGFV, gfvPayload, {
          headers: apiRequestHeaders
          // cancelToken: cancelGFV.value.token
        })
    }

    async function caclculateInterest (calculatorPayload, clientId) {
      if (cancelInterest.value) {
        cancelInterest.value.cancel()
      }
      const urlForCalculator = process.env.VUE_APP_CALCULATOR_URL
      const apiRequestHeaders = {
        'content-type': 'application/json',
        'x-ibm-client-id': clientId,
        client_id: clientId
      }

      cancelInterest.value = axios.CancelToken.source()

      return axios.post(urlForCalculator, calculatorPayload, {
        headers: apiRequestHeaders,
        cancelToken: cancelInterest.value.token
      }).then(
        setTimeout(() => { calculatorRequestProcessing.value = false }, 1000)
      ).catch((e) => { console.log('Error from calculate interest function: ', e) })
    }

    return {
        isMobile,
        onResize,
        termOptions,
        mileageOptions,
        frequencyOptions,
        financeProducts,
        interestRate,
        establishmentFees,
        currencyOptions,
        executeCalculation,
        depositLimit,
        deposit,
        showDepositTip,
        repayments,
        calculatorRequestProcessing,
        total,
        getCurrency
    }
}
